import { useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSEO({
    title: "Contact Us | The Elizabethan Centre, Whaplode Drove",
    description: "Get in touch with The Elizabethan Centre. Located at Parson's Lane, Whaplode Drove, Spalding, Lincolnshire, PE12 0GB. Phone: 01406 709653. Email: elizabethancentre@gmail.com",
    canonical: "https://www.elizabethancentre.co.uk/contact"
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#F5F5F0]" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#14532D]/10 text-[#14532D] text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              Get in Touch
            </span>
            <h1 className="heading-xl mb-4">Contact Us</h1>
            <p className="text-xl text-[#292524]">
              Have a question about booking the hall? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="card-heritage" data-testid="contact-info-card">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#14532D]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#14532D]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-serif text-lg mb-2 text-[#1C1917]">
                        Address
                      </h3>
                      <p className="text-body text-sm">
                        The Elizabethan Centre<br />
                        Parson's Lane<br />
                        Whaplode Drove<br />
                        Spalding<br />
                        Lincolnshire<br />
                        PE12 0GB
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-heritage">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#14532D]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#14532D]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-serif text-lg mb-2 text-[#1C1917]">
                        Phone
                      </h3>
                      <a 
                        href="tel:01406709653" 
                        className="text-body text-sm hover:text-[#14532D] transition-colors"
                      >
                        01406 709653
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-heritage">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#14532D]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#14532D]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-serif text-lg mb-2 text-[#1C1917]">
                        Email
                      </h3>
                      <a 
                        href="mailto:elizabethancentre@gmail.com" 
                        className="text-body text-sm hover:text-[#14532D] transition-colors break-all"
                      >
                        elizabethancentre@gmail.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-heritage">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#14532D]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#14532D]" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-serif text-lg mb-2 text-[#1C1917]">
                        Booking Times
                      </h3>
                      <div className="text-body text-sm space-y-1">
                        <p>Morning: 9am - 1pm</p>
                        <p>Afternoon: 1pm - 5pm</p>
                        <p>Evening: 5pm - 11pm</p>
                        <p>Full Day: 9am - 11pm</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <Card className="card-heritage" data-testid="contact-success">
                  <CardContent className="p-12 text-center">
                    <div className="w-20 h-20 bg-[#DCFCE7] rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-[#15803D]" />
                    </div>
                    <h2 className="heading-md mb-4">Message Sent!</h2>
                    <p className="text-body mb-6">
                      Thank you for getting in touch. We'll respond to your enquiry as soon as possible.
                    </p>
                    <Button 
                      className="btn-primary"
                      onClick={() => setSubmitted(false)}
                    >
                      Send Another Message
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-heritage">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 font-serif">
                      <Send className="w-6 h-6 text-[#14532D]" />
                      Send us a Message
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Name */}
                        <div>
                          <Label htmlFor="name" className="label-heritage">
                            Your Name *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`input-heritage ${errors.name ? 'input-error' : ''}`}
                            placeholder="Enter your name"
                            data-testid="contact-input-name"
                          />
                          {errors.name && (
                            <p className="error-message">{errors.name}</p>
                          )}
                        </div>

                        {/* Email */}
                        <div>
                          <Label htmlFor="email" className="label-heritage">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`input-heritage ${errors.email ? 'input-error' : ''}`}
                            placeholder="your@email.com"
                            data-testid="contact-input-email"
                          />
                          {errors.email && (
                            <p className="error-message">{errors.email}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {/* Phone */}
                        <div>
                          <Label htmlFor="phone" className="label-heritage">
                            Phone Number
                          </Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="input-heritage"
                            placeholder="01234 567890"
                            data-testid="contact-input-phone"
                          />
                        </div>

                        {/* Subject */}
                        <div>
                          <Label htmlFor="subject" className="label-heritage">
                            Subject *
                          </Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            className={`input-heritage ${errors.subject ? 'input-error' : ''}`}
                            placeholder="What is this about?"
                            data-testid="contact-input-subject"
                          />
                          {errors.subject && (
                            <p className="error-message">{errors.subject}</p>
                          )}
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <Label htmlFor="message" className="label-heritage">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          className={`input-heritage min-h-[180px] resize-none ${errors.message ? 'input-error' : ''}`}
                          placeholder="How can we help you?"
                          data-testid="contact-input-message"
                        />
                        {errors.message && (
                          <p className="error-message">{errors.message}</p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={submitting}
                        data-testid="contact-submit-btn"
                      >
                        {submitting ? (
                          <>
                            <span className="spinner mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={18} className="mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="map-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">Find Us</h2>
            <p className="text-body">
              The Elizabethan Centre is located in Whaplode Drove, Lincolnshire
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg h-[400px] sm:h-[450px] lg:h-[500px]" data-testid="google-map-container">
            <iframe
              title="Map showing The Elizabethan Centre at Parson's Lane, Whaplode Drove, PE12 0GB"
              src="https://www.google.com/maps?q=The+Elizabethan+Centre,+Parson's+Lane,+Whaplode+Drove,+Spalding,+PE12+0GB,+UK&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map-iframe"
            />
          </div>
          <div className="text-center mt-6">
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=The+Elizabethan+Centre,+Parson's+Lane,+Whaplode+Drove,+Spalding,+PE12+0GB,+UK"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#14532D] hover:text-[#1C1917] font-medium transition-colors"
              data-testid="get-directions-link"
            >
              <MapPin className="w-4 h-4" />
              Get directions on Google Maps
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
